import * as React from "react";

import "./line-items.scss";

import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {scopedState}         from "@Common/react/scoped-state";
import {CheckShowPricing}    from "@Common/logic/check-show-pricing";
import {scn}                 from "@Common/react/state-change-nested-value";
import {Select}              from "@Common/ui-components/heavy/common/design2/select/select";
import {groupItemEditors}    from "@Common/ui-components/heavy/item-options/minors/group-item-editors";
import {TypeUrl}             from "@Common/ui-components/heavy/item-options/modules/redirect-url/types/type-url/type-url";
import {SliderCheckbox}      from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {changeNestedValue}   from "@Common/utils/arr-path";
import {chain}               from "@Common/utils/fs";
import {equalDeep, omit}     from "@Common/utils/objects";

import {MoneySpan} from "../../../../../common/form/money-span/money-span";
import {cx} from "emotion";

export const LineItems = ({item, lineItems, selectedLineItems, qrEnabled, fullState, next, hidePricing=false}) => cs(
    consumeContext("systemConstants"),
    ["redirectURL", (_, next) => TypeUrl({
        disabled: false,
        liveValue: item?.tracking?.redirectUrl,
        path: ["tracking", "redirectUrl"],
        next
    })],
    ["state", ({}, next) => next(fullState && scopedState(fullState, ["current"]))],
    ({redirectURL, systemConstants, state}) => next({
        itemEditor: groupItemEditors([
            {
                $name: "line items",
                prepareSave: (item) => ({
                    newValue: chain(
                        item,
                        (item) => changeNestedValue(item, ["selections", "options"], (_) => [
                            ..._ ?? [],

                            ...selectedLineItems.map((li) => ({
                                group: "lineItem",
                                dev_key: li.dev_key,
                                subOptionDevKey: state.value.find((l) => li.dev_key === l.dev_key)?.subOptionDevKey,
                            })),
                        ]),
                        (item) => {
                            const qrCode = state.value.find((l) => l.dev_key === "qr_code");
                            if(qrCode) {
                                if(!item.tracking) {
                                    item.tracking = {};
                                }
                                item.tracking.redirectUrl = qrCode.redirectUrl;
                                item.tracking.redirectType = qrCode.redirectType;
                                if(qrCode.redirectType !== "url") {
                                    item.tracking = omit(item.tracking, ['redirectUrl']);
                                }
                            } else if( !qrEnabled.get().value && item.tracking ) {
                                delete item.tracking.redirectUrl;
                                delete item.tracking.redirectType;
                            }
                            return item;
                        },
                    ),
                }),

                changed: !equalDeep(fullState.value.saved, fullState.value.current),

                valid: true,
            },
            ...(subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "url"}) ? [redirectURL.editor] : []),
        ]),
        render: (() => (lineItems.map((lineItem) => (
            <div className="card" key={lineItem.dev_key}>
                {lineItem.isRequired ? (
                    <div className="radio" xd-qa-type="icon" xd-qa-name={lineItem.name}>
                        <i className="fa fa-check"/>
                    </div>
                ) : (
                    <div className="radio">
                        {SliderCheckbox({
                            value: lineItemSelected({value: state.value, dev_key: lineItem.dev_key}),
                            onChange: () => {
                                if( lineItem.dev_key === "qr_code" ) {
                                    if( lineItemSelected({value: state.value, dev_key: lineItem.dev_key}) )
                                        qrEnabled.set(false);
                                    else
                                        qrEnabled.set(true);
                                }
                                scn(state, [], (_) => (
                                    lineItemSelected({value: _, dev_key: lineItem.dev_key}) ? (
                                        _.filter((opt) => opt.dev_key !== lineItem.dev_key)
                                    ) : (
                                        [..._, {
                                            dev_key: lineItem.dev_key,
                                            subOptionDevKey: lineItem.showSubOptions && lineItem.subOptions && lineItem.subOptions[0].dev_key,
                                            ...lineItem.dev_key === "qr_code" ? {redirectType: lineItem.subOptions[0].dev_key} : {},
                                        }]
                                    )
                                ))
                            },
                            xdQaName: lineItem.name,
                        })}
                    </div>
                )}

                <div className="text">
                    <div className="label" xd-qa-type="label" xd-qa-name={lineItem.name}>
                        {lineItem.name}{" "}
                        {cs(
                            ({}, next) => !hidePricing && CheckShowPricing({next}),
                            () => !hidePricing && !lineItem.subOptions && MoneySpan({value: +(lineItem.flatPrice ?? 0)}),
                            () => lineItem.isRequired && lineItem.showSubOptions && state.onChange([...state.value, {
                                dev_key: lineItem.dev_key,
                                subOptionDevKey: lineItem.subOptions[0].dev_key,
                            }]),
                        )}
                    </div>
                    <div className="description" xd-qa-type="label" xd-qa-name="description label">
                        {lineItem.description}
                    </div>
                    {lineItem.showSubOptions && (lineItem?.subOptions?.length === 1 ? (
                        <div className="sub-options-label" xd-qa-type="label" xd-qa-name={lineItem.subOptions && lineItem.subOptions[0].name}>
                            {lineItem.subOptions && lineItem.subOptions[0].name} {!hidePricing && lineItem.subOptions && <span className="price" xd-qa-type="label" xd-qa-name="Price">{MoneySpan({value: +(lineItem.subOptions[0].flatPrice ?? 0)})}</span>}
                            {lineItem.dev_key === "qr_code" && (
                                subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "url"}) ? (
                                    urlInputField({redirectURL})
                                ) : subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "homevalue"}) && item ? (
                                    <div className="preview-link" onClick={() =>
                                        window.open(systemConstants.value?.MN_HOMEVALUE_URL +
                                            encodeURIComponent(btoa(JSON.stringify({
                                                idCompany: item.idCompany,
                                                idOrder: item.itemId,
                                                preview: true,
                                            }))), '_blank')
                                    }>
                                        Preview
                                    </div>
                                ) : subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "xpresslinks"}) && item && (
                                    <div className="preview-link" onClick={() =>
                                        window.open(systemConstants.value?.MN_XPRESSLINKS_URL +
                                            encodeURIComponent(btoa(JSON.stringify({
                                                idCompany: item.idCompany,
                                                idOrder: item.itemId,
                                            }))), '_blank')
                                    }>
                                        Preview
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        lineItem.subOptions && (lineItem.isRequired || lineItemSelected({value: state.value, dev_key: lineItem.dev_key})) && <div className="sub-options">
                            {SubOptionsDropdown({
                                list: lineItem.subOptions,
                                state,
                                dev_key: lineItem.dev_key,
                                hidePricing,
                            })}
                            {lineItem.dev_key === "qr_code" && (
                                subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "url"}) ? (
                                    urlInputField({redirectURL})
                                ) : subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "homevalue"}) && item ? (
                                    <div className="preview-link" onClick={() =>
                                        window.open(systemConstants.value?.MN_HOMEVALUE_URL +
                                            encodeURIComponent(btoa(JSON.stringify({
                                                idCompany: item.idCompany,
                                                idOrder: item.itemId,
                                                preview: true,
                                            }))), '_blank')
                                    }>
                                        Preview
                                    </div>
                                ) : subLineItemSelected({value: state.value, dev_key: "qr_code", subOptionDevKey: "xpresslinks"}) && item && (
                                    <div className="preview-link" onClick={() =>
                                        window.open(systemConstants.value?.MN_XPRESSLINKS_URL +
                                            encodeURIComponent(btoa(JSON.stringify({
                                                idCompany: item.idCompany,
                                                idOrder: item.itemId,
                                            }))), '_blank')
                                    }>
                                        Preview
                                    </div>
                                )
                            )}
                        </div>
                    ))}
                </div>
            </div>
        )))),
    }),
);

const urlInputField = ({redirectURL}) => (
    <div className="url">
        {redirectURL.renderControl({})}
        {redirectURL.errorHandling && (
            <div className="error">
                <div className={cx("message", {inactive: redirectURL.editor.valid})}>
                    {redirectURL.errorHandling.message}
                </div>

                {redirectURL.errorHandling.controls && (
                    <div className="controls">
                        {redirectURL.errorHandling.controls}
                    </div>
                )}
            </div>
        )}
    </div>
)


const SubOptionsDropdown = ({list, state, dev_key, hidePricing}) => Select({
    list,
    itemToLabel: (i) => i.name,
    itemToInfo: (i) => !hidePricing && MoneySpan({value: +(i?.flatPrice ?? 0)}),
    isSelected: (i) => state.value.find((l) => l.dev_key === dev_key && l.subOptionDevKey === i.dev_key),
    onChange: (i) => {
        scn(state, [], (_) => chain(
            _,
            (_) => _.filter((opt) => opt.dev_key !== dev_key),
            (_) => [..._, {
                dev_key,
                subOptionDevKey: i.dev_key,
                ...dev_key === "qr_code" ? {redirectType: i.dev_key} : {},
            }],
        ))
    },
    forcedExpandLeft: true,
    placeholder: "Select...",
    xdQaName: "Sub Options",
})

const lineItemSelected = ({value, dev_key}) => {
    return !!value.find((l) => dev_key === l.dev_key)
}

const subLineItemSelected = ({value, dev_key, subOptionDevKey}) => {
    return !!value.find((l) => dev_key === l.dev_key && subOptionDevKey === l.subOptionDevKey)
}
